export interface DatasetSourceCatalogEntry {
  source_id: number;
  source_name: string;
}

export interface ResolvedDatasetSource {
  sourceId: number;
  sourceName: string;
}

export interface DatasetSourcePreferenceInput {
  visibleSources: readonly DatasetSourceCatalogEntry[];
  persistedSourceName?: string | null;
  persistedSourceId?: number | null;
  sessionSourceName?: string | null;
  sessionSourceId?: number | null;
  /** When true, persisted settings win over session (used on datasets page init). */
  preferPersisted?: boolean;
}

function normalizeName(name: string): string {
  return String(name).trim().toLowerCase();
}

function findSourceByName(
  sources: readonly DatasetSourceCatalogEntry[],
  name: string
): DatasetSourceCatalogEntry | undefined {
  const normalized = normalizeName(name);
  return sources.find((source) => normalizeName(source.source_name) === normalized);
}

function findSourceById(
  sources: readonly DatasetSourceCatalogEntry[],
  id: number
): DatasetSourceCatalogEntry | undefined {
  return sources.find((source) => source.source_id === id);
}

function canSelectAll(sources: readonly DatasetSourceCatalogEntry[]): boolean {
  return sources.length > 1;
}

function resolveSinglePreference(
  sources: readonly DatasetSourceCatalogEntry[],
  name?: string | null,
  id?: number | null
): ResolvedDatasetSource | null {
  const trimmedName = String(name ?? '').trim();

  if (trimmedName === 'ALL') {
    if (canSelectAll(sources)) {
      return { sourceId: 0, sourceName: 'ALL' };
    }
    return null;
  }

  if (trimmedName) {
    const match = findSourceByName(sources, trimmedName);
    if (match) {
      return { sourceId: match.source_id, sourceName: match.source_name };
    }
  }

  const numericId = Number(id);
  if (Number.isFinite(numericId) && numericId > 0) {
    const match = findSourceById(sources, numericId);
    if (match) {
      return { sourceId: match.source_id, sourceName: match.source_name };
    }
  }

  return null;
}

function defaultSelection(sources: readonly DatasetSourceCatalogEntry[]): ResolvedDatasetSource {
  if (canSelectAll(sources)) {
    return { sourceId: 0, sourceName: 'ALL' };
  }

  const onlySource = sources[sources.length - 1];
  return { sourceId: onlySource.source_id, sourceName: onlySource.source_name };
}

/**
 * Resolve the dataset-page source dropdown from persisted settings, session state, or defaults.
 */
export function resolveDatasetSourcePreference(
  input: DatasetSourcePreferenceInput
): ResolvedDatasetSource {
  const { visibleSources, preferPersisted = false } = input;

  if (visibleSources.length === 0) {
    return { sourceId: 0, sourceName: '' };
  }

  const persisted = resolveSinglePreference(
    visibleSources,
    input.persistedSourceName,
    input.persistedSourceId
  );

  const sessionName = String(input.sessionSourceName ?? '').trim();
  const session = resolveSinglePreference(
    visibleSources,
    input.sessionSourceName,
    input.sessionSourceId
  );

  if (preferPersisted) {
    if (persisted) {
      return persisted;
    }
    if (session && sessionName) {
      return session;
    }
    return defaultSelection(visibleSources);
  }

  if (session && sessionName) {
    return session;
  }
  if (persisted) {
    return persisted;
  }

  return defaultSelection(visibleSources);
}

export function datasetSourceSelectionsEqual(
  a: ResolvedDatasetSource,
  b: ResolvedDatasetSource
): boolean {
  return a.sourceId === b.sourceId && a.sourceName === b.sourceName;
}
